import { useEffect, useState } from 'react';
import { useApi } from './useApi';
import type { BlogResponse, BlogFilters } from '@/types/blog';

/**
 * Dynamic hook for fetching filtered site data (blogs, events, etc.)
 * @param filters - Filter parameters for the API call
 * @param dependencies - Additional dependencies to trigger refetch
 */
export function useSiteDataFilters(
  filters: BlogFilters = {},
  dependencies: any[] = []
) {
  return useApi(async () => {
    const { fetchSiteDataFilters } = await import('@/lib/api/client');
    return fetchSiteDataFilters(filters);
  }, [JSON.stringify(filters), ...dependencies]);
}

/**
 * Hook for fetching site data by key
 * @param key - The key to fetch data for
 */
export function useSiteDataByKey(key: string) {
  return useApi(async () => {
    const { fetchSiteDataByKey } = await import('@/lib/api/client');
    return fetchSiteDataByKey(key);
  }, [key]);
}

/**
 * Specific hook for fetching blog posts
 * @param filters - Additional filters for blog posts
 */
export function useBlogPosts(filters: Omit<BlogFilters, 'type'> = {}) {
  return useSiteDataFilters({ ...filters, type: 'blog' });
}

/**
 * Hook for Editor's Picks (top 10 blog posts)
 */
export function useEditorsPicks() {
  return useBlogPosts({ 
    limit: 100, 
    page: 1 
  });
}

/**
 * Hook for fetching events data
 * @param skipPastEvents - Whether to skip past events
 */
export function useEvents(skipPastEvents: boolean = true) {
  return useApi(async () => {
    const { fetchEvents } = await import('@/lib/api/client');
    return fetchEvents(skipPastEvents);
  }, [skipPastEvents]);
}

/**
 * Hook for Help Section data
 */
export function useHelpSection() {
  return useSiteDataByKey('home-help-section');
}

/**
 * Hook for Provider Section data
 */
export function useProviderSection() {
  return useSiteDataByKey('are-you-a-provider');
}

/**
 * Hook for Connect Section data
 */
export function useConnectSection() {
  return useSiteDataByKey('share-your-story');
}

/**
 * Hook for Explore Section data
 */
export function useExploreSection() {
  return useSiteDataByKey('explore-the-caregiver-tech-tool-finder');
}

/**
 * Hook for Partners Section data
 */
export function usePartnersSection() {
  return useSiteDataByKey('current-and-past-partners');
}

/**
 * Hook for Footer data
 */
export function useFooter() {
  return useSiteDataByKey('footer');
}
